import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, effect, inject, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { LocalStorageService, SidebarMenuItem } from '@capturum/ui/api';
import { CapturumSidebarMenuComponent, CapturumSidebarMenuModule } from '@capturum/ui/sidebar-menu';
import { MenuItemEnum } from '@core/enums/layout.enum';
import { AppRoutes } from '@core/enums/routes.enum';
import { MenuItem } from '@core/interfaces/layout.interface';
import { environment } from '@environments/environment';
import { FormlyConfig } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { sortByItemIndex } from '@shared/helpers/array.helper';
import { BroadcastMessageType } from '@shared/interfaces/broadcast-message.interface';
import { BroadcastService } from '@shared/services/broadcast.service';
import { FeedbackWidgetService } from '@shared/services/feedback-widget.service';
import { PageConfigService } from '@shared/services/page-config.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { HeaderComponent } from '../header/header.component';
import { EneqtoLogoComponent } from '../eneqto-logo/eneqto-logo.component';

@Component({
  selector: 'app-basic-layout',
  templateUrl: './basic-layout.component.html',
  styleUrls: ['./basic-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    CapturumSidebarMenuModule,
    HeaderComponent,
    RouterOutlet,
    RouterLink,
    TranslateModule,
    EneqtoLogoComponent,
  ],
})
export class BasicLayoutComponent implements AfterViewInit {
  readonly sidebarMenuRef = viewChild(CapturumSidebarMenuComponent);

  environment = environment;
  menus: SidebarMenuItem[] = [];
  readonly isExpanded = signal(false);
  mobileSidebarExpanded = false;

  readonly #translateService = inject(TranslateService);
  readonly #pageConfigService = inject(PageConfigService);
  readonly #localStorageService = inject(LocalStorageService);
  readonly #formlyConfig = inject(FormlyConfig);
  readonly #ngxPermissionsService = inject(NgxPermissionsService);
  readonly #broadcastService = inject(BroadcastService);
  readonly #router = inject(Router);
  readonly #titleService = inject(Title);
  readonly #feedbackWidgetService = inject(FeedbackWidgetService);

  constructor() {
    this.isExpanded.set(this.#localStorageService.getItem('sidebarExpanded') || false);
    this.mobileSidebarExpanded = this.#localStorageService.getItem('mobileSidebarExpanded') || false;
    this.menus = this.#populateSidebarItems();

    this.#formlyConfig.addConfig({
      validationMessages: [
        { name: 'dtoKey', message: this.#translateService.instant('formly.validation.closing_brackets') },
        { name: 'ip', message: this.#translateService.instant('formly.validation.ip') },
      ],
    });

    effect(() => {
      let pageTitle = '';
      const messages = this.#broadcastService.messages();
      const pageConfig = this.#pageConfigService.pageConfig();

      if (pageConfig?.translated) {
        pageTitle = pageConfig.title;
      } else {
        pageTitle = pageConfig.title ? this.#translateService.instant(pageConfig.title) : null;
      }

      const title = pageTitle ? `${environment.appName} | ${pageTitle}` : environment.appName;

      if (messages?.type === BroadcastMessageType.LOGOUT) {
        this.#router.navigate([AppRoutes.auth, AppRoutes.login]);
      }

      if (pageConfig?.sidebarExpanded !== undefined) {
        this.mobileSidebarExpanded = pageConfig.sidebarExpanded;
        this.#localStorageService.setItem('mobileSidebarExpanded', pageConfig.sidebarExpanded);
      }

      this.#titleService.setTitle(title);
    });
  }

  ngAfterViewInit(): void {
    this.#feedbackWidgetService.createFeedbackWidget();
  }

  setExpandedStatus(expanded: boolean): void {
    this.isExpanded.set(expanded);

    this.#localStorageService.setItem('sidebarExpanded', expanded);
  }

  #populateSidebarItems(): MenuItem[] {
    return [
      {
        label: this.#translateService.stream('menu-item.apps'),
        routerLink: `${AppRoutes.apps}`,
        icon: 'mynaui-layers-three',
        index: MenuItemEnum.Apps,
        hidden: !this.#ngxPermissionsService.getPermission('paas_app.show'),
      },
      {
        label: this.#translateService.stream('menu-item.token'),
        routerLink: `${AppRoutes.token}`,
        icon: 'mynaui-key',
        index: MenuItemEnum.Tokens,
        hidden: !this.#ngxPermissionsService.getPermission('token.show'),
      },
      {
        label: this.#translateService.stream('menu-item.secret'),
        routerLink: `${AppRoutes.secret}`,
        icon: 'mynaui-lock',
        index: MenuItemEnum.Secrets,
        hidden: !this.#ngxPermissionsService.getPermission('secret.show'),
      },
      {
        label: this.#translateService.stream('menu-item.chat'),
        routerLink: `/${AppRoutes.chat}`,
        icon: 'mynaui-message',
        index: MenuItemEnum.Chat,
        hidden: !this.#ngxPermissionsService.getPermission('llm.show'),
      },
      {
        label: this.#translateService.stream('menu-item.flow'),
        routerLink: `${AppRoutes.flowCreator}`,
        icon: 'mynaui-git-branch',
        index: MenuItemEnum.Flows,
        hidden: !this.#ngxPermissionsService.getPermission('flow.show'),
      },
      {
        label: this.#translateService.stream('menu-item.applications'),
        routerLink: `${AppRoutes.application}`,
        icon: 'mynaui-servers',
        index: MenuItemEnum.Applications,
        hidden: !this.#ngxPermissionsService.getPermission('application.show'),
      },
      {
        label: this.#translateService.stream('menu-item.statistics'),
        routerLink: `${AppRoutes.statistic}`,
        icon: 'mynaui-chart-line',
        index: MenuItemEnum.Statistics,
        hidden: !this.#ngxPermissionsService.getPermission('flow_metric.show'),
      },
      {
        label: this.#translateService.stream('menu-item.proxies'),
        routerLink: `${AppRoutes.proxies}`,
        icon: 'mynaui-wifi',
        index: MenuItemEnum.Proxies,
        hidden: !this.#ngxPermissionsService.getPermission('http_proxy.show'),
      },
      {
        label: this.#translateService.stream('menu-item.oauth-clients'),
        routerLink: `${AppRoutes.oAuthClient}`,
        icon: 'mynaui-user-settings',
        index: MenuItemEnum.OAuthClients,
        hidden: !this.#ngxPermissionsService.getPermission('oauth_client.show'),
      },
      {
        label: this.#translateService.stream('menu-item.globals'),
        routerLink: `${AppRoutes.globals}`,
        icon: 'mynaui-package',
        index: MenuItemEnum.Globals,
        hidden: !this.#ngxPermissionsService.getPermission('global.show'),
      },
      {
        label: this.#translateService.stream('menu-item.waf'),
        routerLink: `${AppRoutes.waf}`,
        icon: 'fas fa-shield-halved',
        index: MenuItemEnum.Waf,
        hidden: !this.#ngxPermissionsService.getPermission('waf.show'),
      },
      {
        label: this.#translateService.stream('menu-item.documentation'),
        routerLink: `/${AppRoutes.documentation}`,
        icon: 'mynaui-file-text',
        index: MenuItemEnum.Documentation,
        hidden: false,
      },
    ].sort(sortByItemIndex);
  }
}
